import type { ChangeEvent } from 'react'
import type { DemoSourceMode } from '../../models/demoToneAnalysis'
import type { VideoPreview, VideoPreviewStatus } from '../../models/videoPreview'
import { formatFileSize } from '../../utils/demoAudioSource'

type AnalysisSourceSelectorProps = {
  mode: DemoSourceMode
  youtubeUrl: string
  selectedFile: File | null
  errors: {
    youtubeUrl?: string
    selectedFile?: string
  }
  preview: VideoPreview | null
  previewStatus: VideoPreviewStatus
  previewError: string
  disabled: boolean
  onModeChange: (mode: DemoSourceMode) => void
  onYoutubeUrlChange: (value: string) => void
  onFileChange: (file: File | null) => void
  onRetryPreview: () => void
}

const acceptedAudioExtensions = '.mp3,.wav,.m4a,.flac'

export function AnalysisSourceSelector({
  mode,
  youtubeUrl,
  selectedFile,
  errors,
  preview,
  previewStatus,
  previewError,
  disabled,
  onModeChange,
  onYoutubeUrlChange,
  onFileChange,
  onRetryPreview,
}: AnalysisSourceSelectorProps) {
  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    onFileChange(event.target.files?.[0] ?? null)
  }

  return <div className="ts-analysis-source">
    <div className="ts-analysis-source-tabs" role="tablist" aria-label="Analysis source">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'youtube'}
        className={mode === 'youtube' ? 'active' : ''}
        disabled={disabled}
        onClick={() => onModeChange('youtube')}
      >
        YouTube link
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'file'}
        className={mode === 'file' ? 'active' : ''}
        disabled={disabled}
        onClick={() => onModeChange('file')}
      >
        Audio file
      </button>
    </div>

    {mode === 'youtube'
      ? <div className="ts-analysis-source-field">
          <label>
            <span>YouTube URL</span>
            <input
              value={youtubeUrl}
              onChange={(event) => onYoutubeUrlChange(event.target.value)}
              placeholder="https://www.youtube.com/watch?v=demo"
              disabled={disabled}
            />
          </label>
          {previewStatus === 'loading' && <div className="ts-video-preview-status" role="status" aria-live="polite">
            Loading YouTube preview...
          </div>}
          {previewStatus === 'loaded' && preview && <article className="ts-video-preview-card" aria-label={`${preview.title} preview`}>
            <img src={preview.thumbnailUrl} alt={`${preview.title} video thumbnail`} />
            <div>
              <span>{preview.provider}</span>
              <strong>{preview.title}</strong>
              <small>{preview.duration ? `${preview.channel} - ${preview.duration}` : preview.channel}</small>
            </div>
          </article>}
          {previewStatus === 'error' && <div className="ts-video-preview-error" role="alert">
            <span>{previewError}</span>
            <button type="button" disabled={disabled} onClick={onRetryPreview}>
              Retry preview
            </button>
          </div>}
          {errors.youtubeUrl && <small role="alert">{errors.youtubeUrl}</small>}
        </div>
      : <div className="ts-analysis-source-field">
          <label className="ts-analysis-file-drop">
            <input
              type="file"
              accept={acceptedAudioExtensions}
              aria-label="Choose audio file"
              disabled={disabled}
              onChange={handleFileChange}
            />
            <span>{selectedFile ? selectedFile.name : 'Choose or replace audio file'}</span>
            <b>{selectedFile ? formatFileSize(selectedFile.size) : 'MP3, WAV, M4A, FLAC'}</b>
          </label>
          {selectedFile && <button
            className="ts-analysis-clear-file"
            type="button"
            disabled={disabled}
            onClick={() => onFileChange(null)}
          >
            Clear file
          </button>}
          {errors.selectedFile && <small role="alert">{errors.selectedFile}</small>}
        </div>}
  </div>
}
